import ExternalLinkField from "components/links/ExternalLinkField";

function display(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }
  return String(value);
}

function formatDate(value) {
  if (value == null || value === "") return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function Section({ title, children, id }) {
  return (
    <section
      id={id}
      className="scroll-mt-28 rounded-xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-navy-900/40"
    >
      <h3 className="mb-3 border-b border-gray-100 pb-2 text-xs font-bold uppercase tracking-wider text-brand-600 dark:border-white/10 dark:text-tertiary-400">
        {title}
      </h3>
      {children}
    </section>
  );
}

function FieldGrid({ rows }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
      {rows.map(({ label, value }) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {label}
          </dt>
          <dd className="mt-0.5 break-words text-sm text-navy-800 dark:text-gray-100">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export const GMAIL_INBOX_SECTION_IDS = {
  correo: "gmail-inbox-correo",
  proceso: "gmail-inbox-proceso",
  archivos: "gmail-inbox-archivos",
  notificacion: "gmail-inbox-notificacion",
  enlaces: "gmail-inbox-enlaces",
  gmailMeta: "gmail-inbox-gmail-meta",
  trazabilidad: "gmail-inbox-trazabilidad",
  json: "gmail-inbox-json",
};

function scrollToSection(sectionId) {
  const el = document.getElementById(sectionId);
  if (!el) return;
  if (el instanceof HTMLDetailsElement) {
    el.open = true;
  }
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function GmailInboxSectionNav({ hasStorageUrls }) {
  const entries = [
    { id: GMAIL_INBOX_SECTION_IDS.correo, label: "Correo" },
    { id: GMAIL_INBOX_SECTION_IDS.proceso, label: "Proceso" },
    { id: GMAIL_INBOX_SECTION_IDS.archivos, label: "Archivos" },
    { id: GMAIL_INBOX_SECTION_IDS.notificacion, label: "Notificación" },
    ...(hasStorageUrls
      ? [{ id: GMAIL_INBOX_SECTION_IDS.enlaces, label: "Enlaces storage" }]
      : []),
    { id: GMAIL_INBOX_SECTION_IDS.gmailMeta, label: "Gmail / IDs" },
    { id: GMAIL_INBOX_SECTION_IDS.trazabilidad, label: "Trazabilidad" },
    { id: GMAIL_INBOX_SECTION_IDS.json, label: "JSON" },
  ];

  return (
    <nav
      className="sticky top-0 z-[2] -mx-1 mb-1 rounded-xl border border-gray-200/80 bg-white/90 px-3 py-3 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-navy-800/95"
      aria-label="Secciones del correo recibido"
    >
      <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Ir a sección
      </p>
      <div className="flex flex-wrap gap-2">
        {entries.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => scrollToSection(id)}
            className="rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 text-left text-xs font-medium text-brand-700 transition hover:border-brand-400 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-400/50 dark:border-brand-500/40 dark:bg-navy-900/80 dark:text-tertiary-300 dark:hover:border-tertiary-400 dark:hover:bg-navy-800"
          >
            {label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function statusLabel(s) {
  const k = String(s ?? "").toLowerCase();
  const map = {
    received: "Recibido",
    parsed: "Parseado",
    validated: "Validado",
    accepted: "Aceptado",
    rejected: "Rechazado",
    error: "Error",
  };
  return map[k] || s || "—";
}

/**
 * Detalle de una fila `gmail_dte_inbox` — mismos patrones de sección / navegación que DTE emitidos.
 */
export function GmailInboxDetailFields({ item }) {
  if (!item) return null;

  const attachmentText = Array.isArray(item.attachment_names)
    ? item.attachment_names.join(", ")
    : "—";

  const hasStorageUrls = Boolean(
    item.incoming_xml_url ||
      item.incoming_supplier_pdf_url ||
      item.respuesta_intercambio_url ||
      item.recepcion_mercaderias_url ||
      item.resultado_aprobacion_comercial_url
  );

  const correo = [
    { label: "ID", value: display(item.id) },
    { label: "Asunto", value: display(item.subject) },
    { label: "De", value: display(item.from_email) },
    { label: "Para / casilla", value: display(item.to_email) },
    { label: "Casilla monitoreada", value: display(item.email_address) },
    {
      label: "Recibido",
      value: formatDate(item.received_at) ?? display(item.received_at),
    },
  ];

  const proceso = [
    { label: "Estado proceso", value: statusLabel(item.processing_status) },
    { label: "Nota proceso", value: display(item.processing_note) },
    { label: "Cod. envío", value: display(item.cod_envio) },
  ];

  const archivos = [
    { label: "Tiene XML", value: item.has_xml ? "Sí" : "No" },
    { label: "Tiene PDF", value: item.has_pdf ? "Sí" : "No" },
    { label: "XML archivo", value: display(item.xml_filename) },
    { label: "PDF archivo", value: display(item.pdf_filename) },
    { label: "Adjuntos (nombres)", value: attachmentText },
  ];

  const notificacion = [
    { label: "Estado", value: display(item.notification_status) },
    { label: "Error", value: display(item.notification_error) },
    {
      label: "Enviado",
      value: formatDate(item.notification_sent_at) ?? "—",
    },
    { label: "Message ID", value: display(item.notification_message_id) },
  ];

  const gmailMeta = [
    { label: "Gmail message id", value: display(item.gmail_message_id) },
    { label: "Gmail thread id", value: display(item.gmail_thread_id) },
    { label: "Gmail history id", value: display(item.gmail_history_id) },
  ];

  const trazabilidad = [
    { label: "Creado", value: formatDate(item.created_at) ?? "—" },
    { label: "Actualizado", value: formatDate(item.updated_at) ?? "—" },
  ];

  return (
    <div className="space-y-5">
      <GmailInboxSectionNav hasStorageUrls={hasStorageUrls} />

      <Section id={GMAIL_INBOX_SECTION_IDS.correo} title="Correo y casilla">
        <FieldGrid rows={correo} />
      </Section>

      <Section id={GMAIL_INBOX_SECTION_IDS.proceso} title="Proceso e intercambio">
        <FieldGrid rows={proceso} />
      </Section>

      <Section id={GMAIL_INBOX_SECTION_IDS.archivos} title="Archivos y adjuntos">
        <FieldGrid rows={archivos} />
      </Section>

      <Section id={GMAIL_INBOX_SECTION_IDS.notificacion} title="Notificación interna">
        <FieldGrid rows={notificacion} />
      </Section>

      {hasStorageUrls ? (
        <Section id={GMAIL_INBOX_SECTION_IDS.enlaces} title="Enlaces storage">
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <ExternalLinkField
              fieldLabel="XML entrante"
              url={item.incoming_xml_url}
            />
            <ExternalLinkField
              fieldLabel="PDF proveedor"
              url={item.incoming_supplier_pdf_url}
            />
            <ExternalLinkField
              fieldLabel="Respuesta intercambio"
              url={item.respuesta_intercambio_url}
            />
            <ExternalLinkField
              fieldLabel="Recepción mercaderías"
              url={item.recepcion_mercaderias_url}
            />
            <ExternalLinkField
              fieldLabel="Resultado aprobación comercial"
              url={item.resultado_aprobacion_comercial_url}
            />
          </div>
        </Section>
      ) : null}

      <Section id={GMAIL_INBOX_SECTION_IDS.gmailMeta} title="Identificadores Gmail">
        <FieldGrid rows={gmailMeta} />
      </Section>

      <Section id={GMAIL_INBOX_SECTION_IDS.trazabilidad} title="Trazabilidad">
        <FieldGrid rows={trazabilidad} />
      </Section>

      <details
        id={GMAIL_INBOX_SECTION_IDS.json}
        className="scroll-mt-28 rounded-lg border border-gray-200 bg-lightPrimary p-3 dark:border-white/10 dark:bg-navy-900"
      >
        <summary className="cursor-pointer text-xs font-semibold text-gray-600 dark:text-gray-300">
          Respuesta JSON completa
        </summary>
        <pre className="mt-2 max-h-64 overflow-auto text-[11px] leading-snug text-navy-800 dark:text-gray-300">
          {JSON.stringify(item, null, 2)}
        </pre>
      </details>
    </div>
  );
}
